import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Alert,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import { LocalShipping as CarrierIcon, Summarize as SummaryIcon } from '@mui/icons-material';
import { Carrier, AWB, Shipment, FinalSubmissionPayload } from '../types';
import { ServiceGroup } from './Step2ServicesAWB';
import { createD365PurchaseInvoice, createPurchaseInvoice } from '../../../pages/PurchaseInvoice/api/api';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import DatePicker from '@Components/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrAfter);
import SbTextField from '@Components/Textfield';
import SbButton from '@Components/Button';
import { SBTable } from '@Components/Table/Table';
import { TableHeadCell, TableRowCell } from '@Components/Table/TableCell';
import { SBTableRow } from '@Components/Table/TableRow';
import SBDropdown from '@Components/Dropdown';
import { getCarrierDetails, splitTaxOptions } from './constant';
import { getInvoiceSettings } from '@Reducers/OrganizationSetup';

interface Props {
  selectedCarrier: Carrier | null;
  awbs: AWB[];
  services: ServiceGroup[];
  invoiceDate: Dayjs | null;
  setInvoiceDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  dueDate: Dayjs | null;
  setDueDate: React.Dispatch<React.SetStateAction<Dayjs | null>>;
  taxation: string;
  setTaxation: React.Dispatch<React.SetStateAction<string>>;
  handleReset: () => void;
  setServiceProviderData: React.Dispatch<React.SetStateAction<Shipment[]>>;
  setServices: React.Dispatch<React.SetStateAction<ServiceGroup[]>>;
  departmentCode: string;
  setDepartmentCode: React.Dispatch<React.SetStateAction<string>>;
  locationId: string;
  setLocationId: React.Dispatch<React.SetStateAction<string>>;
}

const Step3ReviewSubmit: React.FC<Props> = ({
  selectedCarrier,
  awbs,
  invoiceDate,
  setInvoiceDate,
  dueDate,
  setDueDate,
  taxation,
  setTaxation,
  handleReset,
  setServiceProviderData,
  setServices,
  departmentCode,
  setDepartmentCode,
  locationId,
  setLocationId,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const carrierOptions = useSelector((state: RootStateType) => state.purchaseCarrier.carrierOptions);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const { invoiceSetting } = useSelector((state: RootStateType) => state?.orgSetup);
  const { taxInformation = [] } = invoiceSetting || {};
  const taxInformationOptions = taxInformation.map(s => ({ display: s.display, value: s.key }));
  const [taxBreakdown, setTaxBreakdown] = React.useState<string>('');
  const [splitTax, setSplitTax] = React.useState<string>('');
  const SystemList = useSelector((state: RootStateType) => state.System.data.selectedList).map(system => ({
    display: system.name,
    value: system.d365Department || '',
    id: system._id,
  }));
  const _systems = useSelector((state: RootStateType) => state.loginUser?.profile?._systems);

  const activeSystemList = useMemo(() => {
    if (_systems?.length) return SystemList.filter(system => _systems.includes(system.id));
    else return [];
  }, [SystemList, _systems]);

  const carrierDetails = useMemo(() => {
    if (!selectedCarrier?.id) {
      return {
        d365AccountDisplayValue: '',
        vendorLocationMap: [],
      };
    }

    return getCarrierDetails(carrierOptions, selectedCarrier.id);
  }, [carrierOptions, selectedCarrier]);

  // Get primary currency from the first AWB
  const primaryCurrency = awbs.length > 0 ? awbs[0].currency : 'INR';

  // Calculate totals
  const subtotal = awbs.reduce((sum, awb) => sum + (awb.carrierCharges || 0), 0);

  // Calculate total weight
  const totalWeight = awbs.reduce((sum, awb) => sum + (awb.carrierWeight || 0), 0);

  const handleSubmit = async (isD365?: boolean) => {
    setSubmitted(true);

    if (!invoiceDate || !dueDate || !taxation || awbs.length === 0) return;

    setIsSubmitting(true);

    const finalPayload: FinalSubmissionPayload = {
      carrier: selectedCarrier!,
      invoiceDate,
      dueDate,
      carrierReferenceNumber: taxation,
      awbs,
      ...(isD365 && {
        invoiceLocation: departmentCode,
        vendorLocation: locationId,
        d365AccountDisplayValue: carrierDetails.d365AccountDisplayValue,
      }),
    };

    try {
      if (!isD365) {
        await createPurchaseInvoice(finalPayload);
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Success',
            subTitle: 'Purchase invoice created successfully',
            navigateTo: '/purchase-invoices',
          }),
        );
      } else {
        await createD365PurchaseInvoice(finalPayload);
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Success',
            subTitle: 'D365 Purchase invoice created successfully',
            navigateTo: '/purchase-invoices',
          }),
        );
      }

      setServiceProviderData([]);
      setServices([]);
      handleReset();
      setDueDate(null);
      setInvoiceDate(null);
      setTaxation('');
    } catch (err: any) {
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: err?.response?.data?._message || err?.response?.data?.msg || 'Something went wrong',
          subTitle: err?.response?.data?.errorDetails || 'Please try after sometimes',
        }),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    !!invoiceDate &&
    !!dueDate &&
    dayjs(dueDate).isSameOrAfter(dayjs(invoiceDate), 'day') &&
    !!taxation &&
    awbs.length > 0;

  const showError = (field: 'invoice' | 'due', fieldValue: Dayjs | null | string) => {
    if (!submitted) return '';

    if (!fieldValue) return 'Required field';

    // Allow due date to be the same day, but not earlier
    if (field === 'due' && invoiceDate && dayjs(fieldValue).isBefore(dayjs(invoiceDate), 'day')) {
      return 'Due date must be same or after Invoice date';
    }

    return '';
  };

  useEffect(() => {
    dispatch(getInvoiceSettings());
  }, []);

  return (
    <Box>
      <Stack spacing={4}>
        {/* Header */}
        <Box>
          <Typography variant="body1" color="text.secondary">
            Please review all information below before submitting your purchase invoice
          </Typography>
        </Box>

        {/* Carrier Information Card */}
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            p: 1.5,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <CarrierIcon color="primary" />
            <Typography fontWeight={600}>Carrier Information :</Typography>
            {selectedCarrier ? (
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
                <Typography fontWeight={600}>{selectedCarrier.name?.toUpperCase()},</Typography>
                <Typography fontWeight={600}>Currency: {primaryCurrency}</Typography>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ m: 0 }}>
                No carrier selected
              </Alert>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 2fr',
            gap: '5rem',
          }}
        >
          {/* Invoice Details Card */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {/* Invoice Date and Due Date side by side */}
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Invoice Date"
                  value={invoiceDate}
                  handleChange={newValue => setInvoiceDate(newValue)}
                  required
                  error={showError('invoice', invoiceDate)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Due Date"
                  value={dueDate}
                  handleChange={newValue => setDueDate(newValue)}
                  required
                  error={showError('due', dueDate)}
                  minDate={dayjs(invoiceDate)}
                />
              </Grid>

              {/* Carrier Reference Number below */}
              <Grid item xs={12}>
                <SbTextField
                  placeholder="Enter Carrier Reference Number"
                  label="Carrier Reference Number"
                  name="altContactName"
                  onChange={e => setTaxation(e.target.value)}
                  value={taxation}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <SBDropdown
                  id="departmentCode"
                  value={[departmentCode]}
                  label="Invoice Location"
                  onChange={([value]) => {
                    if (!value) {
                      dispatch(
                        SuccessErrorModalOpen({
                          type: 'error',
                          title: 'Warning',
                          subTitle: 'Department Code is missing for this system.',
                        }),
                      );
                    }
                    setDepartmentCode(value);
                  }}
                  options={activeSystemList}
                  placeholder="Select Department Code"
                />
              </Grid>

              <Grid item xs={12}>
                <SBDropdown
                  id="locationId"
                  value={[locationId]}
                  label="Vendor Location"
                  onChange={([value]) => {
                    setLocationId(value);
                  }}
                  options={
                    carrierDetails.vendorLocationMap.map(loc => ({ value: loc.locationId, display: loc.name })) || []
                  }
                  placeholder="Select Location"
                />
              </Grid>
              <Grid item xs={12}>
                <SBDropdown
                  label="Select Taxation"
                  id="taxBreakdown"
                  placeholder="Select"
                  options={taxInformationOptions || []}
                  onChange={(value: string[]) => setTaxBreakdown(value[0] || '')}
                  name="taxBreakdown"
                  value={taxBreakdown ? [taxBreakdown] : []}
                />
              </Grid>
              <Grid item xs={12}>
                <SBDropdown
                  label="Tax Breakdown"
                  id="splitTax"
                  placeholder="Select"
                  options={splitTaxOptions || []}
                  onChange={(value: string[]) => setSplitTax(value[0] || '')}
                  name="splitTax"
                  value={splitTax ? [splitTax] : []}
                />
              </Grid>
              {/* Validation Alert */}
              <Grid item xs={12}>
                <SbButton
                  variant="contained"
                  size="large"
                  onClick={() => handleSubmit(true)}
                  disabled={!isFormValid || !departmentCode || !locationId}
                  sx={{
                    color: 'primary',
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Send to 365'}
                </SbButton>
                {!isFormValid && (
                  <Typography sx={{ fontSize: '12px', color: '#444141ff', marginTop: '1rem' }}>
                    Please complete all required fields (Invoice Date, Due Date) and add Carrier Reference Number before
                    submitting. Due date must be same or after Invoice date
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* AWB Summary Card */}
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <SummaryIcon color="primary" />
                <Typography variant="h6" fontWeight="600">
                  AWB Summary ({awbs.length} items)
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="right">
                  Total Amount - {primaryCurrency} {subtotal.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="right" display="block">
                  Total Weight - {totalWeight.toFixed(1)} kg
                </Typography>
              </Stack>
            </Stack>

            {awbs.length > 0 ? (
              <>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    overflowY: 'auto',
                    maxHeight: '410px',
                  }}
                >
                  <SBTable size="medium">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableHeadCell sx={{ fontWeight: '600', fontSize: '0.95rem' }}>Shipment Number</TableHeadCell>
                        <TableHeadCell sx={{ fontWeight: '600', fontSize: '0.95rem' }} align="center">
                          Origin
                        </TableHeadCell>
                        <TableHeadCell sx={{ fontWeight: '600', fontSize: '0.95rem' }} align="center">
                          Destination
                        </TableHeadCell>
                        <TableHeadCell sx={{ fontWeight: '600', fontSize: '0.95rem' }} align="right">
                          Weight (kg)
                        </TableHeadCell>
                        <TableHeadCell sx={{ fontWeight: '600', fontSize: '0.95rem' }} align="right">
                          Charges
                        </TableHeadCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {awbs.map((a, index) => (
                        <SBTableRow
                          key={a.id}
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            backgroundColor: index % 2 === 0 ? 'transparent' : alpha(theme.palette.action.hover, 0.02),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            },
                          }}
                        >
                          <TableRowCell align="center" className="md-text capitalize">
                            {a.number}
                          </TableRowCell>
                          <TableRowCell align="center" className="md-text capitalize">
                            {a.origin}
                          </TableRowCell>
                          <TableRowCell align="center" className="md-text capitalize">
                            {a.destination}
                          </TableRowCell>
                          <TableRowCell align="center" className="md-text capitalize">
                            <Typography align="center" className="md-text">
                              {a.carrierWeight?.toFixed(1) || '0.0'}
                            </Typography>
                          </TableRowCell>
                          <TableRowCell align="center" className="md-text capitalize">
                            <Typography align="center" className="md-text">
                              {a.currency} {a.carrierCharges?.toFixed(2) || '0.00'}
                            </Typography>
                          </TableRowCell>
                        </SBTableRow>
                      ))}
                    </TableBody>
                  </SBTable>
                </TableContainer>
              </>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                No AWB items added. Please go back to previous steps to add AWB information.
              </Alert>
            )}
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          <SbButton
            variant="outlined"
            size="large"
            onClick={handleReset}
            sx={{
              minWidth: 120,
              borderRadius: 2,
              fontWeight: '600',
            }}
          >
            Cancel
          </SbButton>
          <SbButton
            variant="contained"
            // color="primary.main"
            size="large"
            onClick={() => handleSubmit()}
            disabled={!isFormValid}
            // startIcon={<Send />}
            sx={{
              color: 'primary',
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create Invoice'}
          </SbButton>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Step3ReviewSubmit;
